import { useState, useCallback, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { StoreModelsFilters } from '@/components/StoreModels/StoreModelsFilters'
import { StoreModelsGrid } from '@/components/StoreModels/StoreModelsGrid'
import { Pagination } from '@/components/common/Pagination'
import { usePersistedPageSize } from '@/hooks/usePersistedPageSize'
import { TABLE_PAGE_SIZE_KEYS } from '@/stores/useTablePageSizeStore'
import { useStoreModelsList } from '@/hooks/useStoreModels'
import { useProcessingStatus } from '@/hooks/useProcessingStatus'
import type { HierarchyFilterValue } from '@/components/AssortmentPlansGrid/HierarchyFilter'
import { queryKeys } from '@/lib/queryKeys'
import { toast } from '@/stores/useToastStore'
import type {
  ModelType,
  ModelStatus,
  StoreModelRead,
  ProcessingResponse,
} from '@/types'
import type { StoreModelLocationFilters } from '@/api/storeModels'

export default function AdminStoreModels() {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()

  // Capture processing response from navigation state
  const [processingResponse] = useState<ProcessingResponse | null>(
    () =>
      (location.state as { processingResponse?: ProcessingResponse })
        ?.processingResponse ?? null
  )

  // Clear router state so refresh doesn't re-trigger SSE
  useEffect(() => {
    if (location.state?.processingResponse) {
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const processingStatus = useProcessingStatus(processingResponse)

  // Invalidate list cache and show toast when processing completes or errors
  useEffect(() => {
    if (processingStatus.state === 'complete') {
      queryClient.invalidateQueries({ queryKey: queryKeys.storeModels.all })
      toast.success('Model created successfully')
    } else if (processingStatus.state === 'error') {
      queryClient.invalidateQueries({ queryKey: queryKeys.storeModels.all })
      toast.error(processingStatus.errorMessage ?? 'Model processing failed')
    }
  }, [processingStatus.state, processingStatus.errorMessage, queryClient])

  // Filter state
  const [searchInput, setSearchInput] = useState('')
  const [modelType, setModelType] = useState<ModelType | 'all'>('all')
  const [status, setStatus] = useState<ModelStatus | 'all'>('all')
  const [locations, setLocations] = useState<StoreModelLocationFilters>({})
  const [hierarchy, setHierarchy] = useState<HierarchyFilterValue>({
    departmentId: null,
    classId: null,
    subclassId: null,
  })
  const hierarchyId =
    hierarchy.subclassId ??
    hierarchy.classId ??
    hierarchy.departmentId ??
    undefined
  const [sort, setSort] = useState('-created_at')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = usePersistedPageSize(
    TABLE_PAGE_SIZE_KEYS.ADMIN_STORE_MODELS_LIST,
    20
  )

  // Reset to page 1 when filters change. `locations` is an object rebuilt on
  // every change, so it is keyed by value — an identity check would reset the
  // page on unrelated re-renders.
  const locationsKey = JSON.stringify(locations)
  const hierarchyKey = JSON.stringify(hierarchy)
  useEffect(() => {
    setPage(1)
  }, [
    searchInput,
    modelType,
    status,
    locationsKey,
    hierarchyKey,
    sort,
    pageSize,
  ])

  const { data, isLoading, isError } = useStoreModelsList({
    search: searchInput,
    modelType,
    status,
    locations,
    hierarchyId,
    sort,
    page,
    pageSize,
  })

  useEffect(() => {
    if (isError) {
      toast.error('Failed to load store models')
    }
  }, [isError])

  const models = data?.data ?? []
  const total = data?.pagination.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  const handleRowClicked = useCallback(
    (model: StoreModelRead) => {
      navigate(`/store-models/${model.id}`)
    },
    [navigate]
  )

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            Manage Store Models
          </h1>
          <p className="text-base text-muted-foreground mt-1">
            View and manage store allocation models
          </p>
        </div>
        <Button
          className="gap-2 cursor-pointer"
          onClick={() => navigate('/store-models/create')}
        >
          <Plus className="h-4 w-4" />
          Create Model
        </Button>
      </div>

      {/* Filter Bar */}
      <StoreModelsFilters
        searchInput={searchInput}
        onSearchChange={setSearchInput}
        modelType={modelType}
        onModelTypeChange={setModelType}
        status={status}
        onStatusChange={setStatus}
        locations={locations}
        onLocationsChange={setLocations}
        hierarchy={hierarchy}
        onHierarchyChange={setHierarchy}
        sort={sort}
        onSortChange={setSort}
      />

      {/* Grid */}
      <StoreModelsGrid
        models={models}
        onRowClicked={handleRowClicked}
        loading={isLoading}
        processingModelId={
          processingStatus.state === 'processing'
            ? (processingResponse?.resource_id ?? null)
            : null
        }
      />

      {/* Pagination */}
      <Pagination
        page={page}
        pageSize={pageSize}
        totalPages={totalPages}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />
    </div>
  )
}
